import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import api from "../../lib/api";
import PageHeader from "../../components/PageHeader";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import { Receipt, Wallet, ChartLineUp, FileText, Coins, Bank, ArrowsClockwise, Plus, Trash, Warning } from "@phosphor-icons/react";

const RAILS = ["upi", "card", "netbanking", "neft", "rtgs", "cash", "dd", "van"];

export default function Fees() {
  const { t } = useTranslation();
  const [tab, setTab] = useState("structure");
  const [heads, setHeads] = useState([]);
  const [demands, setDemands] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [payments, setPayments] = useState([]);
  const [scholarships, setScholarships] = useState([]);
  const [refunds, setRefunds] = useState([]);
  const [overdue, setOverdue] = useState([]);
  const [recon, setRecon] = useState({ unreconciled: [], suspense: [] });
  const [headForm, setHeadForm] = useState({ code: "", name: "", amount: 0, program: "", schedule: "semester", gst_pct: 0 });
  const [dnForm, setDnForm] = useState({ student_id: "", fee_codes: "", due_date: "" });
  const [payForm, setPayForm] = useState({ demand_note_id: "", rail: "upi", amount: 0, txn_ref: "" });
  const [schForm, setSchForm] = useState({ name: "", student_id: "", amount: 0, scheme: "" });
  const [refForm, setRefForm] = useState({ payment_id: "", amount: 0, reason: "" });

  const load = useCallback(async () => {
    const [h, d, i, p, s, r, o, rec] = await Promise.all([
      api.get("/fees/structure"), api.get("/fees/demand-notes"), api.get("/fees/invoices"),
      api.get("/fees/payments"), api.get("/fees/scholarships"), api.get("/fees/refunds"),
      api.get("/fees/overdue"), api.get("/fees/reconciliation"),
    ]);
    setHeads(h.data); setDemands(d.data); setInvoices(i.data); setPayments(p.data);
    setScholarships(s.data); setRefunds(r.data); setOverdue(o.data); setRecon(rec.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveHead = async () => {
    if (!headForm.code || !headForm.name) return toast.error("Code & name required");
    try { await api.post("/fees/structure", { ...headForm, amount: Number(headForm.amount), gst_pct: Number(headForm.gst_pct), program: headForm.program || null });
      toast.success("Fee head saved");
      setHeadForm({ code: "", name: "", amount: 0, program: "", schedule: "semester", gst_pct: 0 }); load();
    } catch { toast.error("Failed"); }
  };

  const delHead = async (code) => {
    if (!window.confirm(`Delete head ${code}?`)) return;
    try { await api.delete(`/fees/structure/${code}`); toast.success("Deleted"); load(); }
    catch { toast.error("Failed"); }
  };

  const genDn = async () => {
    if (!dnForm.student_id || !dnForm.fee_codes || !dnForm.due_date) return toast.error("All required");
    try {
      const { data } = await api.post("/fees/demand-notes", {
        student_id: dnForm.student_id,
        fee_codes: dnForm.fee_codes.split(",").map(s => s.trim()).filter(Boolean),
        due_date: dnForm.due_date,
      });
      toast.success(`Demand note ${data.demand_note_id} · ₹${data.total.toLocaleString()}`);
      setDnForm({ student_id: "", fee_codes: "", due_date: "" }); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const genInvoice = async (dn_id) => {
    try { await api.post(`/fees/invoices/${dn_id}`); toast.success("Invoice generated"); load(); }
    catch { toast.error("Failed"); }
  };

  const recordPay = async () => {
    if (!payForm.demand_note_id || !payForm.amount) return toast.error("Demand note + amount required");
    try {
      const { data } = await api.post("/fees/payments", { ...payForm, amount: Number(payForm.amount) });
      toast.success(`₹${data.payment.amount} recorded via ${data.payment.rail} · outstanding ₹${data.outstanding}`);
      setPayForm({ demand_note_id: "", rail: "upi", amount: 0, txn_ref: "" }); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const applyHold = async (dn_id) => {
    try { await api.post(`/fees/dunning/${dn_id}/hold`); toast.success("Academic hold applied"); load(); }
    catch { toast.error("Failed"); }
  };

  const reconcile = async (pay_id) => {
    try { await api.post(`/fees/reconciliation/${pay_id}/reconcile`); toast.success("Reconciled"); load(); }
    catch { toast.error("Failed"); }
  };

  const saveSch = async () => {
    if (!schForm.name || !schForm.student_id) return toast.error("Name & student required");
    try { await api.post("/fees/scholarships", { ...schForm, amount: Number(schForm.amount) });
      toast.success("Scholarship created");
      setSchForm({ name: "", student_id: "", amount: 0, scheme: "" }); load();
    } catch { toast.error("Failed"); }
  };

  const approveSch = async (id, status) => {
    try { await api.patch(`/fees/scholarships/${id}`, { status }); toast.success(status); load(); }
    catch { toast.error("Failed"); }
  };

  const saveRef = async () => {
    if (!refForm.payment_id || !refForm.amount || !refForm.reason) return toast.error("All required");
    try { await api.post("/fees/refunds", { ...refForm, amount: Number(refForm.amount) });
      toast.success("Refund requested");
      setRefForm({ payment_id: "", amount: 0, reason: "" }); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const updateRef = async (id, status) => {
    try { await api.patch(`/fees/refunds/${id}`, { status }); toast.success(status); load(); }
    catch { toast.error("Failed"); }
  };

  const tabs = [
    { v: "structure", l: "Fee Structure", I: FileText },
    { v: "demand", l: "Demand Notes", I: Receipt },
    { v: "invoices", l: "Invoices", I: FileText },
    { v: "checkout", l: "Payments", I: Wallet },
    { v: "scholarships", l: "Scholarships", I: Coins },
    { v: "dunning", l: "Dunning", I: Warning },
    { v: "reconciliation", l: "Reconcile", I: ArrowsClockwise },
    { v: "refunds", l: "Refunds", I: Bank },
  ];

  return (
    <div>
      <PageHeader overline="M04 · Identity & Finance" title={t("modules.fees.title")} description={t("modules.fees.subtitle")} />
      <div className="p-6 lg:p-8 space-y-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-border border border-border rounded-md overflow-hidden">
          {[
            { l: "Fee heads", v: heads.length, I: FileText },
            { l: "Active demands", v: demands.filter(d => d.status !== "paid").length, I: Receipt },
            { l: "Total collected", v: `₹${payments.reduce((s, p) => s + (p.amount || 0), 0).toLocaleString()}`, I: ChartLineUp },
            { l: "Overdue", v: overdue.length, I: Warning },
          ].map(it => (
            <div key={it.l} className="bg-white p-5">
              <it.I size={20} weight="duotone" className="text-[hsl(244_53%_20%)]"/>
              <div className="eos-overline mt-3">{it.l}</div>
              <div className="font-heading font-extrabold text-2xl mt-1 tracking-tight">{it.v}</div>
            </div>
          ))}
        </div>

        <div className="inline-flex flex-wrap border border-border rounded-md overflow-hidden" data-testid="fees-tabs">
          {tabs.map(it => (
            <button key={it.v} onClick={() => setTab(it.v)}
              className={`px-4 py-2 text-sm inline-flex items-center gap-1.5 border-r border-border last:border-r-0 ${tab === it.v ? "bg-[hsl(244_53%_20%)] text-white" : "bg-white hover:bg-secondary"}`}
              data-testid={`fees-tab-${it.v}`}>
              <it.I size={14}/> {it.l}
            </button>
          ))}
        </div>

        {tab === "structure" && (
          <div className="grid lg:grid-cols-2 gap-4" data-testid="structure-pane">
            <div className="eos-card">
              <div className="font-heading font-bold text-base mb-3">Add fee head</div>
              {[["code", "Code"], ["name", "Name"], ["amount", "Amount"], ["program", "Program (opt)"], ["gst_pct", "GST %"]].map(([k, l]) => (
                <div key={k} className="mb-2"><label className="eos-overline">{l}</label><input value={headForm[k]} onChange={(e) => setHeadForm(f => ({ ...f, [k]: e.target.value }))} className="mt-1 w-full px-2 py-2 text-sm border border-border rounded-md" data-testid={`head-${k}`}/></div>
              ))}
              <div className="mb-2"><label className="eos-overline">Schedule</label>
                <select value={headForm.schedule} onChange={(e) => setHeadForm(f => ({ ...f, schedule: e.target.value }))} className="mt-1 w-full px-2 py-2 text-sm border border-border rounded-md bg-white" data-testid="head-schedule">
                  {["one_time", "semester", "yearly"].map(s => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
                </select>
              </div>
              <Button onClick={saveHead} className="w-full bg-[hsl(244_53%_20%)] hover:bg-[hsl(244_53%_15%)] mt-2" data-testid="save-head-btn"><Plus size={14} className="mr-1.5"/> Save</Button>
            </div>
            <div className="eos-card p-0 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-secondary text-muted-foreground"><tr>{["Code", "Name", "Amount", "GST", "Schedule", ""].map(h => <th key={h} className="text-left px-3 py-2 text-[10px] uppercase tracking-[0.15em]">{h}</th>)}</tr></thead>
                <tbody>
                  {heads.map(h => (
                    <tr key={h.code} className="border-t border-border" data-testid={`head-row-${h.code}`}>
                      <td className="px-3 py-2 font-mono-eos text-[11px]">{h.code}</td>
                      <td className="px-3 py-2">{h.name}</td>
                      <td className="px-3 py-2 font-mono-eos">₹{h.amount?.toLocaleString()}</td>
                      <td className="px-3 py-2 font-mono-eos">{h.gst_pct}%</td>
                      <td className="px-3 py-2 text-[11px]">{h.schedule}</td>
                      <td className="px-3 py-2"><Button variant="ghost" onClick={() => delHead(h.code)} className="h-6 w-6 p-0 text-rose-600" data-testid={`del-head-${h.code}`}><Trash size={11}/></Button></td>
                    </tr>
                  ))}
                  {heads.length === 0 && <tr><td colSpan="6" className="p-6 text-center text-sm text-muted-foreground">No fee heads.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "demand" && (
          <div className="space-y-4" data-testid="demand-pane">
            <div className="eos-card">
              <div className="font-heading font-bold text-base mb-3">Generate demand note</div>
              <div className="grid grid-cols-3 gap-2">
                {[["student_id", "Student ID"], ["fee_codes", "Fee codes (csv)"], ["due_date", "Due date YYYY-MM-DD"]].map(([k, l]) => (
                  <div key={k}><label className="eos-overline">{l}</label><input value={dnForm[k]} onChange={(e) => setDnForm(f => ({ ...f, [k]: e.target.value }))} className="mt-1 w-full px-2 py-2 text-sm border border-border rounded-md" data-testid={`dn-${k}`}/></div>
                ))}
              </div>
              <Button onClick={genDn} className="mt-3 bg-[hsl(20_90%_48%)] hover:bg-[hsl(20_90%_42%)]" data-testid="gen-dn-btn">Generate</Button>
            </div>
            <div className="eos-card p-0 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-secondary text-muted-foreground"><tr>{["DN ID", "Student", "Total", "Outstanding", "Status", "Due", ""].map(h => <th key={h} className="text-left px-3 py-2 text-[10px] uppercase tracking-[0.15em]">{h}</th>)}</tr></thead>
                <tbody>
                  {demands.map(d => (
                    <tr key={d.demand_note_id} className="border-t border-border" data-testid={`dn-row-${d.demand_note_id}`}>
                      <td className="px-3 py-2 font-mono-eos text-[11px]">{d.demand_note_id}</td>
                      <td className="px-3 py-2 font-mono-eos text-[11px]">{d.student_id}</td>
                      <td className="px-3 py-2 font-mono-eos">₹{d.total?.toLocaleString()}</td>
                      <td className="px-3 py-2 font-mono-eos">₹{d.outstanding?.toLocaleString()}</td>
                      <td className="px-3 py-2"><span className={`text-[10px] px-1.5 py-0.5 rounded ${d.status === "paid" ? "bg-emerald-50 text-emerald-700" : d.status === "partial" ? "bg-amber-50 text-amber-700" : "bg-secondary"}`}>{d.status}</span></td>
                      <td className="px-3 py-2 text-[11px] text-muted-foreground">{d.due_date}</td>
                      <td className="px-3 py-2"><Button variant="outline" onClick={() => genInvoice(d.demand_note_id)} className="h-6 px-2 text-[10px]" data-testid={`gen-inv-${d.demand_note_id}`}>Invoice</Button></td>
                    </tr>
                  ))}
                  {demands.length === 0 && <tr><td colSpan="7" className="p-6 text-center text-sm text-muted-foreground">No demand notes.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "invoices" && (
          <div className="eos-card p-0 overflow-hidden" data-testid="invoices-pane">
            <table className="w-full text-sm">
              <thead className="bg-secondary text-muted-foreground"><tr>{["Invoice", "Student", "Subtotal", "GST", "Total", "IRN", "Issued"].map(h => <th key={h} className="text-left px-3 py-2 text-[10px] uppercase tracking-[0.15em]">{h}</th>)}</tr></thead>
              <tbody>
                {invoices.map(i => (
                  <tr key={i.invoice_id} className="border-t border-border" data-testid={`inv-row-${i.invoice_id}`}>
                    <td className="px-3 py-2 font-mono-eos text-[11px]">{i.invoice_id}</td>
                    <td className="px-3 py-2 font-mono-eos text-[11px]">{i.student_id}</td>
                    <td className="px-3 py-2 font-mono-eos">₹{i.subtotal?.toLocaleString()}</td>
                    <td className="px-3 py-2 font-mono-eos">₹{i.gst_total?.toLocaleString()}</td>
                    <td className="px-3 py-2 font-mono-eos font-medium">₹{i.total?.toLocaleString()}</td>
                    <td className="px-3 py-2 font-mono-eos text-[10px] text-muted-foreground">{i.irn?.slice(0, 12)}…</td>
                    <td className="px-3 py-2 text-[11px] text-muted-foreground">{new Date(i.issued_at).toLocaleDateString()}</td>
                  </tr>
                ))}
                {invoices.length === 0 && <tr><td colSpan="7" className="p-6 text-center text-sm text-muted-foreground">No invoices yet — generate from Demand Notes.</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {tab === "checkout" && (
          <div className="space-y-4" data-testid="checkout-pane">
            <div className="eos-card">
              <div className="font-heading font-bold text-base mb-3">Record payment (unified checkout)</div>
              <div className="grid grid-cols-4 gap-2">
                <div><label className="eos-overline">Demand note ID</label><input value={payForm.demand_note_id} onChange={(e) => setPayForm(f => ({ ...f, demand_note_id: e.target.value }))} className="mt-1 w-full px-2 py-2 text-sm border border-border rounded-md" data-testid="pay-dn"/></div>
                <div><label className="eos-overline">Rail</label><select value={payForm.rail} onChange={(e) => setPayForm(f => ({ ...f, rail: e.target.value }))} className="mt-1 w-full px-2 py-2 text-sm border border-border rounded-md bg-white" data-testid="pay-rail">{RAILS.map(r => <option key={r}>{r}</option>)}</select></div>
                <div><label className="eos-overline">Amount</label><input value={payForm.amount} onChange={(e) => setPayForm(f => ({ ...f, amount: e.target.value }))} className="mt-1 w-full px-2 py-2 text-sm border border-border rounded-md" data-testid="pay-amount"/></div>
                <div><label className="eos-overline">Txn ref (opt)</label><input value={payForm.txn_ref} onChange={(e) => setPayForm(f => ({ ...f, txn_ref: e.target.value }))} className="mt-1 w-full px-2 py-2 text-sm border border-border rounded-md" data-testid="pay-ref"/></div>
              </div>
              <Button onClick={recordPay} className="mt-3 bg-[hsl(20_90%_48%)] hover:bg-[hsl(20_90%_42%)]" data-testid="record-pay-btn"><Wallet size={14} className="mr-1.5"/> Record payment</Button>
            </div>
            <div className="eos-card p-0 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-secondary text-muted-foreground"><tr>{["Pay ID", "Student", "Rail", "Amount", "Ref", "Reconciled", "Date"].map(h => <th key={h} className="text-left px-3 py-2 text-[10px] uppercase tracking-[0.15em]">{h}</th>)}</tr></thead>
                <tbody>
                  {payments.map(p => (
                    <tr key={p.payment_id} className="border-t border-border" data-testid={`pay-row-${p.payment_id}`}>
                      <td className="px-3 py-2 font-mono-eos text-[11px]">{p.payment_id}</td>
                      <td className="px-3 py-2 font-mono-eos text-[11px]">{p.student_id}</td>
                      <td className="px-3 py-2"><span className="text-[10px] bg-secondary px-1.5 py-0.5 rounded uppercase">{p.rail}</span></td>
                      <td className="px-3 py-2 font-mono-eos">₹{p.amount?.toLocaleString()}</td>
                      <td className="px-3 py-2 font-mono-eos text-[10px] text-muted-foreground">{p.txn_ref}</td>
                      <td className="px-3 py-2"><span className={`text-[10px] ${p.reconciled ? "text-emerald-700" : "text-amber-700"}`}>{p.reconciled ? "✓" : "pending"}</span></td>
                      <td className="px-3 py-2 text-[11px] text-muted-foreground">{new Date(p.received_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                  {payments.length === 0 && <tr><td colSpan="7" className="p-6 text-center text-sm text-muted-foreground">No payments.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "scholarships" && (
          <div className="space-y-4" data-testid="scholarships-pane">
            <div className="eos-card">
              <div className="font-heading font-bold text-base mb-3">New scholarship application</div>
              <div className="grid grid-cols-4 gap-2">
                {[["name", "Name"], ["student_id", "Student ID"], ["amount", "Amount"], ["scheme", "Scheme (PM-USP / State / Institutional)"]].map(([k, l]) => (
                  <div key={k}><label className="eos-overline">{l}</label><input value={schForm[k]} onChange={(e) => setSchForm(f => ({ ...f, [k]: e.target.value }))} className="mt-1 w-full px-2 py-2 text-sm border border-border rounded-md" data-testid={`sch-${k}`}/></div>
                ))}
              </div>
              <Button onClick={saveSch} className="mt-3 bg-[hsl(244_53%_20%)] hover:bg-[hsl(244_53%_15%)]" data-testid="save-sch-btn"><Plus size={14} className="mr-1.5"/> Submit</Button>
            </div>
            <div className="eos-card p-0 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-secondary text-muted-foreground"><tr>{["ID", "Name", "Student", "Scheme", "Amount", "Status", ""].map(h => <th key={h} className="text-left px-3 py-2 text-[10px] uppercase tracking-[0.15em]">{h}</th>)}</tr></thead>
                <tbody>
                  {scholarships.map(s => (
                    <tr key={s.scholarship_id} className="border-t border-border" data-testid={`sch-row-${s.scholarship_id}`}>
                      <td className="px-3 py-2 font-mono-eos text-[11px]">{s.scholarship_id}</td>
                      <td className="px-3 py-2">{s.name}</td>
                      <td className="px-3 py-2 font-mono-eos text-[11px]">{s.student_id}</td>
                      <td className="px-3 py-2 text-[11px]">{s.scheme || "—"}</td>
                      <td className="px-3 py-2 font-mono-eos">₹{s.amount?.toLocaleString()}</td>
                      <td className="px-3 py-2"><span className={`text-[10px] px-1.5 py-0.5 rounded ${s.status === "approved" || s.status === "disbursed" ? "bg-emerald-50 text-emerald-700" : s.status === "rejected" ? "bg-rose-50 text-rose-700" : "bg-amber-50 text-amber-700"}`}>{s.status}</span></td>
                      <td className="px-3 py-2 space-x-1">
                        {s.status === "pending" && <>
                          <Button variant="ghost" onClick={() => approveSch(s.scholarship_id, "approved")} className="h-6 px-2 text-[10px] text-emerald-700" data-testid={`approve-${s.scholarship_id}`}>Approve</Button>
                          <Button variant="ghost" onClick={() => approveSch(s.scholarship_id, "rejected")} className="h-6 px-2 text-[10px] text-rose-600" data-testid={`reject-${s.scholarship_id}`}>Reject</Button>
                        </>}
                        {s.status === "approved" && <Button variant="ghost" onClick={() => approveSch(s.scholarship_id, "disbursed")} className="h-6 px-2 text-[10px] text-indigo-700" data-testid={`disburse-${s.scholarship_id}`}>Disburse</Button>}
                      </td>
                    </tr>
                  ))}
                  {scholarships.length === 0 && <tr><td colSpan="7" className="p-6 text-center text-sm text-muted-foreground">No scholarships.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "dunning" && (
          <div className="eos-card p-0 overflow-hidden" data-testid="dunning-pane">
            <div className="p-4 border-b border-border flex items-center gap-2">
              <Warning size={16} weight="duotone" className="text-amber-600"/>
              <div className="font-heading font-semibold">Overdue demand notes ({overdue.length})</div>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-secondary text-muted-foreground"><tr>{["DN", "Student", "Outstanding", "Due", ""].map(h => <th key={h} className="text-left px-3 py-2 text-[10px] uppercase tracking-[0.15em]">{h}</th>)}</tr></thead>
              <tbody>
                {overdue.map(d => (
                  <tr key={d.demand_note_id} className="border-t border-border" data-testid={`overdue-${d.demand_note_id}`}>
                    <td className="px-3 py-2 font-mono-eos text-[11px]">{d.demand_note_id}</td>
                    <td className="px-3 py-2 font-mono-eos text-[11px]">{d.student_id}</td>
                    <td className="px-3 py-2 font-mono-eos text-rose-700">₹{d.outstanding?.toLocaleString()}</td>
                    <td className="px-3 py-2 text-[11px] text-rose-700">{d.due_date}</td>
                    <td className="px-3 py-2"><Button variant="outline" onClick={() => applyHold(d.demand_note_id)} className="h-6 px-2 text-[10px] text-rose-700" data-testid={`hold-${d.demand_note_id}`}>Apply hold</Button></td>
                  </tr>
                ))}
                {overdue.length === 0 && <tr><td colSpan="5" className="p-6 text-center text-sm text-emerald-700">All clear — no overdues.</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {tab === "reconciliation" && (
          <div className="eos-card p-0 overflow-hidden" data-testid="reconciliation-pane">
            <div className="p-4 border-b border-border">
              <div className="font-heading font-semibold">Unreconciled payments ({recon.unreconciled_count})</div>
              <div className="text-[11px] text-muted-foreground">UPI/card/netbanking auto-reconcile. Cash/DD/NEFT/RTGS/VAN need manual reconciliation.</div>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-secondary text-muted-foreground"><tr>{["Pay ID", "Student", "Rail", "Amount", "Date", ""].map(h => <th key={h} className="text-left px-3 py-2 text-[10px] uppercase tracking-[0.15em]">{h}</th>)}</tr></thead>
              <tbody>
                {recon.unreconciled.map(p => (
                  <tr key={p.payment_id} className="border-t border-border" data-testid={`unrec-${p.payment_id}`}>
                    <td className="px-3 py-2 font-mono-eos text-[11px]">{p.payment_id}</td>
                    <td className="px-3 py-2 font-mono-eos text-[11px]">{p.student_id}</td>
                    <td className="px-3 py-2 uppercase text-[11px]">{p.rail}</td>
                    <td className="px-3 py-2 font-mono-eos">₹{p.amount?.toLocaleString()}</td>
                    <td className="px-3 py-2 text-[11px] text-muted-foreground">{new Date(p.received_at).toLocaleDateString()}</td>
                    <td className="px-3 py-2"><Button onClick={() => reconcile(p.payment_id)} className="h-6 px-2 text-[10px] bg-emerald-600 hover:bg-emerald-700" data-testid={`rec-${p.payment_id}`}>Reconcile</Button></td>
                  </tr>
                ))}
                {recon.unreconciled.length === 0 && <tr><td colSpan="6" className="p-6 text-center text-sm text-emerald-700">All reconciled.</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {tab === "refunds" && (
          <div className="space-y-4" data-testid="refunds-pane">
            <div className="eos-card">
              <div className="font-heading font-bold text-base mb-3">Request refund</div>
              <div className="grid grid-cols-3 gap-2">
                {[["payment_id", "Payment ID"], ["amount", "Amount"], ["reason", "Reason"]].map(([k, l]) => (
                  <div key={k}><label className="eos-overline">{l}</label><input value={refForm[k]} onChange={(e) => setRefForm(f => ({ ...f, [k]: e.target.value }))} className="mt-1 w-full px-2 py-2 text-sm border border-border rounded-md" data-testid={`ref-${k}`}/></div>
                ))}
              </div>
              <Button onClick={saveRef} className="mt-3 bg-[hsl(244_53%_20%)] hover:bg-[hsl(244_53%_15%)]" data-testid="save-ref-btn">Request</Button>
            </div>
            <div className="eos-card p-0 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-secondary text-muted-foreground"><tr>{["ID", "Payment", "Amount", "Status", "Reason", ""].map(h => <th key={h} className="text-left px-3 py-2 text-[10px] uppercase tracking-[0.15em]">{h}</th>)}</tr></thead>
                <tbody>
                  {refunds.map(r => (
                    <tr key={r.refund_id} className="border-t border-border" data-testid={`ref-row-${r.refund_id}`}>
                      <td className="px-3 py-2 font-mono-eos text-[11px]">{r.refund_id}</td>
                      <td className="px-3 py-2 font-mono-eos text-[11px]">{r.payment_id}</td>
                      <td className="px-3 py-2 font-mono-eos">₹{r.amount?.toLocaleString()}</td>
                      <td className="px-3 py-2"><span className={`text-[10px] px-1.5 py-0.5 rounded ${r.status === "completed" || r.status === "approved" ? "bg-emerald-50 text-emerald-700" : r.status === "rejected" ? "bg-rose-50 text-rose-700" : "bg-amber-50 text-amber-700"}`}>{r.status}</span></td>
                      <td className="px-3 py-2 text-[11px] text-muted-foreground">{r.reason}</td>
                      <td className="px-3 py-2 space-x-1">
                        {r.status === "pending" && <>
                          <Button variant="ghost" onClick={() => updateRef(r.refund_id, "approved")} className="h-6 px-2 text-[10px] text-emerald-700" data-testid={`appr-ref-${r.refund_id}`}>Approve</Button>
                          <Button variant="ghost" onClick={() => updateRef(r.refund_id, "rejected")} className="h-6 px-2 text-[10px] text-rose-600" data-testid={`rej-ref-${r.refund_id}`}>Reject</Button>
                        </>}
                        {r.status === "approved" && <Button variant="ghost" onClick={() => updateRef(r.refund_id, "completed")} className="h-6 px-2 text-[10px] text-indigo-700" data-testid={`comp-ref-${r.refund_id}`}>Complete</Button>}
                      </td>
                    </tr>
                  ))}
                  {refunds.length === 0 && <tr><td colSpan="6" className="p-6 text-center text-sm text-muted-foreground">No refunds.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
